/**
 * The one list. Swipe order, tab-bar slot order and the URL↔index mapping all
 * derive from it, so they cannot drift apart — the same rule the iOS client
 * enforces with a test on `AppTab.allCases`.
 */
export type TabId = "feed" | "diary" | "share" | "profile";

export type Tab = {
  id: TabId;
  path: string;
  labelKey: string;
};

export const TABS: readonly Tab[] = [
  { id: "feed", path: "/feed", labelKey: "shell.feed" },
  // The diary keeps /dashboard: "/" serves signed-out visitors and must stay
  // the public product page.
  { id: "diary", path: "/dashboard", labelKey: "shell.diary" },
  // The film lives here and nowhere else — one scrub, one home, the rule
  // iOS's export tab states. It sits after the diary so the diary stays one
  // swipe from both of its neighbours.
  { id: "share", path: "/share", labelKey: "shell.share" },
  { id: "profile", path: "/profile", labelKey: "shell.profile" },
] as const;

/**
 * Where the app opens. Named rather than derived from a position: with four
 * tabs there is no middle, and the diary is the app regardless of how many
 * neighbours it grows — the same reason iOS names `AppTab.launch`.
 */
export const DIARY_INDEX = 1;

function normalize(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

export function tabIndexForPath(pathname: string): number | null {
  const path = normalize(pathname);
  const index = TABS.findIndex((tab) => tab.path === path);
  return index === -1 ? null : index;
}

export function tabAtIndex(index: number): Tab | null {
  return TABS[index] ?? null;
}
